'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Check } from 'lucide-react'
import Link from 'next/link'

export function Pricing() {
  const [annual, setAnnual] = useState(true)

  return (
    <section id="pricing" className="py-24 bg-white dark:bg-encore-gray-900">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 text-encore-gray-900 dark:text-white">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-encore-gray-600 dark:text-encore-gray-400 max-w-2xl mx-auto mb-8">
            Start free, upgrade as you grow. No hidden fees.
          </p>

          <div className="flex items-center justify-center gap-4">
            <span className={`text-sm font-medium ${!annual ? 'text-encore-gray-900 dark:text-white' : 'text-gray-500'}`}>Monthly</span>
            <button
              onClick={() => setAnnual(!annual)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-encore-primary focus:ring-offset-2 ${
                annual ? 'bg-encore-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  annual ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${annual ? 'text-encore-gray-900 dark:text-white' : 'text-gray-500'}`}>
              Yearly <span className="text-emerald-500 text-xs font-bold ml-1">(Save 20%)</span>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Starter */}
          <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-8 flex flex-col hover:border-gray-300 transition-colors">
            <h3 className="text-xl font-bold mb-2">Starter</h3>
            <p className="text-gray-500 text-sm mb-6">For single locations getting started.</p>
            <div className="mb-6">
              <span className="text-4xl font-bold">{annual ? '$29' : '$35'}</span>
              <span className="text-gray-500">/mo</span>
            </div>
            <Button asChild variant="outline" className="w-full mb-8">
              <Link href="/register">Start Free Trial</Link>
            </Button>
            <ul className="space-y-3 flex-1">
              {[
                '1 Location',
                'Basic AI Feedback',
                'QR Survey Generator',
                'Email Support',
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-3 text-sm">
                  <Check className="h-4 w-4 text-green-500 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Pro */}
          <div className="relative rounded-2xl border-2 border-encore-primary bg-encore-gray-50 dark:bg-gray-800/50 p-8 flex flex-col shadow-xl scale-105 z-10">
            <div className="absolute top-0 right-0 bg-encore-primary text-white text-xs font-bold px-3 py-1 rounded-bl-xl rounded-tr-lg">
              POPULAR
            </div>
            <h3 className="text-xl font-bold mb-2">Professional</h3>
            <p className="text-gray-500 text-sm mb-6">For growing businesses.</p>
            <div className="mb-6">
              <span className="text-4xl font-bold">{annual ? '$79' : '$95'}</span>
              <span className="text-gray-500">/mo</span>
            </div>
            <Button asChild className="w-full mb-8 bg-encore-gradient hover:opacity-90">
              <Link href="/register">Start Free Trial</Link>
            </Button>
            <ul className="space-y-3 flex-1">
              {[
                'Up to 3 Locations',
                'Advanced AI Responses',
                'Deep Analytics Dashboard',
                'Competitor Analysis',
                'Priority Support',
                'Integrations (POS)',
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-3 text-sm">
                  <Check className="h-4 w-4 text-encore-primary shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Enterprise */}
          <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-8 flex flex-col hover:border-gray-300 transition-colors">
            <h3 className="text-xl font-bold mb-2">Enterprise</h3>
            <p className="text-gray-500 text-sm mb-6">For franchises & chains.</p>
            <div className="mb-6">
              <span className="text-4xl font-bold">Custom</span>
            </div>
            <Button asChild variant="outline" className="w-full mb-8">
              <Link href="mailto:sales@encore-ai.com">Contact Sales</Link>
            </Button>
            <ul className="space-y-3 flex-1">
              {[
                'Unlimited Locations',
                'Custom AI Models',
                'Dedicated Account Manager',
                'SLA & Uptime Guarantee',
                'API Access',
                'White Labeling',
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-3 text-sm">
                  <Check className="h-4 w-4 text-green-500 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}

